import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { tap } from 'rxjs/operators';

import { takeUntilDestroy } from '../../common/take-until-destroy.decorator';
import {
    autoFit,
    autoSizeColumns,
    autoSizeRows,
    bindWidthResize,
    Column,
    ExperimentalNgGridComponent,
} from '../grid';
import { PerformerItemView } from '../main/model/performer-item-view.model';
import { TrendDialogMode } from '../trend-dialog';

import { createTableData } from './function/create-grid-data.function';
import { ColumnId } from './model/column-id.model';
import { TableColumn } from './model/performer-data-column.model';
import { SortOptions } from './model/sort-options.model';
import { TableData } from './model/table-data.model';
import { TranslationKeyMap } from './table.i18n';

@Component({
    selector: 'cmps-mi2-table',
    templateUrl: './table.component.html',
    host: {
        class: 'cmps-mi2-table',
    },
})
export class TableComponent implements OnChanges, OnInit, OnDestroy {
    @ViewChild(ExperimentalNgGridComponent)
    private readonly _grid: ExperimentalNgGridComponent;

    @Input() data: PerformerItemView[];
    @Input() altData: PerformerItemView[];
    @Input() sortOptions: SortOptions;
    @Input() popupOpen: boolean; // TODO maybe remove
    @Output() columnSortClick: EventEmitter<{
        c: TableColumn;
        sortOrder?: 'asc' | 'desc';
    }>;

    @Output() showTrendClick: EventEmitter<{
        r: PerformerItemView;
        key: TrendDialogMode;
    }>;

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _ColumnId = ColumnId;

    /** @private */
    _TrendDialogMode = TrendDialogMode;

    /** @private */
    _tableData: TableData;

    constructor(
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
    ) {
        this.columnSortClick = new EventEmitter();
        this.showTrendClick = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        let dataChange = false;
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('data')) {
            this._tableData = createTableData(this.data, this.sortOptions);
            dataChange = true;
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('altData')) {
            this._tableData?.rows.forEach((r) => {
                /* cells[n].ctx.data is still referencing original this.data[n] object */
                r.hidden = this.altData.every((e) => e !== r.cells[0].ctx.data);
                r.ctx.index = this.altData.findIndex(
                    (e) => e === r.cells[0].ctx.data,
                );
            });
            if (!dataChange) {
                if (this._tableData != null) {
                    this._tableData.rows = [...this._tableData.rows];
                    this._tableData.sortDefs = [...this._tableData.sortDefs];
                }
                this._onResize();
            } else {
                this._tableData?.rows.sort((a, b) => a.ctx.index - b.ctx.index);
            }
        }
    }

    ngOnInit(): void {
        bindWidthResize(this._el.nativeElement)
            .pipe(
                tap(() => this._onResize()),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.columnSortClick.complete();
        this.showTrendClick.complete();
    }

    /** @private */
    _onWillInflate(): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const el = this._el.nativeElement.querySelector(
            '[ngGrid]',
        ) as HTMLElement;
        if (el) {
            el.style.visibility = 'hidden';
        }
    }

    /** @private */
    _onDidInflate(): void {
        this._changeDetectorRef.detectChanges();
        if (!this._grid || !this._grid.renderer) {
            return;
        }

        const flatColumns = this._tableData.rendererOpts.columns.reduce<
            Column[]
        >((p, c) => {
            return p.concat(c.children);
        }, []);

        // this is dirty. will try to improve the grid api
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._grid.renderer
            .layout()
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => autoSizeColumns(flatColumns, this._grid.renderer))
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this._grid.renderer.layout())
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() =>
                autoSizeRows(
                    [
                        ...this._grid.renderer.contentColumns.columns.children,
                    ].reverse(),
                    this._grid.renderer,
                    null,
                    {},
                ),
            )
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() =>
                autoSizeRows(
                    this._grid.gridNode.children,
                    this._grid.renderer,
                    flatColumns.filter(
                        (e) => e.id === ColumnId.IMPROVEMENT_CATEGORY,
                    ),
                    {},
                ),
            )
            .then(() => {
                const left = this._grid.renderer.rowcache.left.filter((e) =>
                    this._grid.gridNode.children.some((ee) => ee.def === e.def),
                );
                left.forEach((e) => {
                    if (e.renderContext?.element != null) {
                        e.renderContext.element.style.height = `${e.def.height}px`;
                    }
                });
            })
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this._grid.renderer.layout())
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => autoFit(this._grid.renderer))
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this._grid.renderer.layout())
            .catch((err) => console.warn(err))
            .then(() => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                const el = this._el.nativeElement.querySelector(
                    '[ngGrid]',
                ) as HTMLElement;
                if (el) {
                    el.style.visibility = '';
                }
            });
    }

    private _onResize(): void {
        this._onWillInflate();
        this._onDidInflate();
    }
}
